import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import { useQueryClient } from '@tanstack/react-query'

import { OrgPermission, TeamMemberRole } from '@makyoapp/makyo-sdk/models/ACL'
import { MemberAdapter, MemberField, PaginatedItems } from '@makyoapp/makyo-common'

import { queryKeys } from '@/lib/react-query'
import { toasted } from '@/lib/toast'
import { useRemoveMemberMutation, useUpdateRoleMutation } from '@/resolvers/org/mutation'
import { useGetOrgTeams } from '@/resolvers/org/query'
import { useEditTeam } from '@/resolvers/org/teams/api'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { useParamId } from '@/hooks/useParamId'
import { useReqError } from '@/hooks/useReqError'
import { ORG_ROUTE, ORG_TEAM_ROUTE } from '@/shared/constants'
import { useQueryParam } from '@/shared/makyo-hooks'

/**
 * @function useDetailTeamMember
 * @description A custom React Hook to manage the details of a team member.
 *
 * @returns {Object} Various states and handlers:
 *  - {boolean} canAddTeamMember - A permission state to check if a team member can be added.
 *  - {Object} currentOrg - The current organization object.
 *  - {boolean} isLoading - A state to control the loading state.
 *  - {string} search - A state to handle search queries.
 *  - {Object} team - The current team object.
 *  - {Object} teamMembers - An object holding the team members.
 *  - {string} teamPath - The path to the team.
 *  - {function} handleRemoveMember - A function to handle the removal of a member.
 *  - {function} handleUpdateRole - A function to handle the updating of a member's role.
 *  - {function} resetParams - A function to reset the search parameters.
 *  - {function} setIsLoading - A function to set the loading state.
 *  - {function} setSearch - A function to set the search query.
 */
const useDetailTeamMember = () => {
  // Fetching organization ID and team ID
  const orgId = useParamId('org')
  const teamId = useParamId('team')

  // Constructing team path
  const teamPath = `${ORG_ROUTE}/${orgId}/${ORG_TEAM_ROUTE}`

  // Using translation hook
  const { t } = useTranslation()

  // Error handling
  const printAPIError = useReqError()

  // Using context to get and update the current organization
  const { currentOrg, updateCurrentOrg } = useCurrentOrgContext()

  // Checking permission to add team member
  const canAddTeamMember = useOrgPerm(OrgPermission.TeamsAddMembers)
  const canEditTeam = useOrgPerm(OrgPermission.TeamsEditTeamInfo)

  // Get Org Teams
  const { data: orgTeams } = useGetOrgTeams()

  // Fetching the team from the current organization
  const team = orgTeams?.find((cot) => cot.id === teamId)

  // Setting up loading state
  const [isLoading, setIsLoading] = useBoolean(false)

  // Team Name inline editing state
  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Setting up search query related states and handlers
  const { debounceSearch, search, setSearch, resetParams } = useQueryParam<MemberField>()

  // Setting up team members state
  const [teamMembers, setTeamMembers] = useState<PaginatedItems<MemberAdapter>>({
    items: [],
    totalCount: 0,
  })

  //define mutations
  const { mutate: updateRoleMutation } = useUpdateRoleMutation()
  const { mutate: removeMemberMutation } = useRemoveMemberMutation()

  const queryClient = useQueryClient()

  /*const handleList = useCallback(() => {
    if (teamId) {
      setIsLoading(true)
      TeamsAPI.ReqGet(teamId)
        .then((team) => {
          const t = fromSdkTeam(team)
          setTeam(t)
        })
        .catch((e) => {
          printAPIError(e)
        })
        .finally(() => setIsLoading(false))
    }
  }, [teamId, setIsLoading, setTeam])

  useEffect(() => {
    handleList()
  }, [handleList])*/

  // Effect to update team members based on search query
  useEffect(() => {
    const activeMembers: MemberAdapter[] = team?.members.filter((m) => m.joined !== undefined) || []

    setTeamMembers({
      items: debounceSearch
        ? activeMembers.filter((m) => [m.name, m.email].join(' ').includes(debounceSearch))
        : activeMembers,
      totalCount: team?.members.length || 0,
    })
  }, [debounceSearch, team, setTeamMembers])

  // Handler for updating a team member's role
  const handleUpdateRole = (member: MemberAdapter, newRole: TeamMemberRole, closeModal: () => void) => {
    updateRoleMutation(
      { teamId, memberId: member.id, newRole },
      {
        onSuccess() {
          closeModal()
          toasted(
            t('Member {{member}} role updated!', {
              member: member.name,
            })
          )
          queryClient.invalidateQueries({
            queryKey: [queryKeys.orgTeams, orgId],
          })
          updateCurrentOrg({})
        },
        onError(e: any) {
          printAPIError(e)
        },
      }
    )
  }

  // Handler for removing a team member
  const handleRemoveMember = (member: MemberAdapter, closeModal: () => void) => {
    removeMemberMutation(
      { teamId, memberId: member.id },
      {
        onSuccess() {
          closeModal()
          toasted(t('Member {{member}} removed!', { member: member.name }))
          queryClient.invalidateQueries({
            queryKey: [queryKeys.orgTeams, orgId],
          })
          updateCurrentOrg({
            onBeforeUpdate: () => setIsLoading(true),
            onAfterUpdate: () => setIsLoading(false),
          })
        },
        onError(e: any) {
          printAPIError(e)
        },
      }
    )
  }

  // Handler for editing team name
  const handleEditTeamNameSubmit = useEditTeam({
    editTeam: team,
    onBeforeUpdate: useCallback(() => {
      setIsLoading(true)
    }, [setIsLoading]),
    onAfterUpdate: useCallback(() => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.orgTeams, orgId],
      })
      setIsLoading(false)
    }, [queryClient, orgId, setIsLoading]),
  })

  const handleEditTeamName = useCallback(
    (newName: string) => {
      if (team) {
        handleEditTeamNameSubmit(newName)
      }
    },
    [team, handleEditTeamNameSubmit]
  )

  // Auto-focus and select input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleDoubleClick = useCallback(() => {
    if (canEditTeam && team) {
      setEditedName(team.name)
      setIsEditing(true)
    }
  }, [canEditTeam, team])

  const handleSave = useCallback(() => {
    if (team && editedName.trim() && editedName !== team.name) {
      handleEditTeamName(editedName.trim())
    }
    setIsEditing(false)
  }, [team, editedName, handleEditTeamName])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSave()
      } else if (e.key === 'Escape') {
        setIsEditing(false)
      }
    },
    [handleSave]
  )

  return {
    //states
    canAddTeamMember,
    canEditTeam,
    currentOrg,
    isLoading,
    search,
    team,
    teamMembers,
    teamPath,
    // Team Name inline editing states
    isEditing,
    editedName,
    inputRef,
    //methods
    handleRemoveMember,
    handleUpdateRole,
    handleEditTeamName,
    resetParams,
    setIsLoading,
    setSearch,
    // Team Name inline editing methods
    setEditedName,
    handleDoubleClick,
    handleSave,
    handleKeyDown,
  }
}

export default useDetailTeamMember
